/**
 * Ufakturert Tid - Oversikt over arbeid som ikke er fakturert
 *
 * Funksjonalitet:
 * - Viser total ufakturert tid og verdi
 * - Liste per sak med detaljer
 * - Eksport til CSV
 * - Filtrer per sak
 */

import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, FileText, Download, AlertCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.stig-958.workers.dev';

export default function UfakturertTid() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSak, setSelectedSak] = useState(null);
  const [expandedSak, setExpandedSak] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ufakturert-tid`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Feil ved lasting av ufakturert tid');
      }

      const result = await response.json();
      setData(result);

    } catch (error) {
      console.error('Ufakturert tid error:', error);
      alert('Kunne ikke laste ufakturert tid');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ufakturert-tid/export`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Feil ved eksport');
      }

      const result = await response.json();

      // Last ned CSV
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export error:', error);
      alert('Kunne ikke eksportere til CSV');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Laster...</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <Clock size={32} />
        Ufakturert Tid
      </h1>

      {/* Totaler */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '15px',
        marginBottom: '30px'
      }}>
        <StatCard
          icon={<Clock size={24} color="#007bff" />}
          title="Total timer"
          value={data.totaler.total_timer.toFixed(2) + ' t'}
          color="#007bff"
        />
        <StatCard
          icon={<DollarSign size={24} color="#28a745" />}
          title="Total verdi"
          value={'kr ' + data.totaler.total_verdi.toLocaleString('no-NO', { minimumFractionDigits: 2 })}
          color="#28a745"
        />
        <StatCard
          icon={<FileText size={24} color="#6f42c1" />}
          title="Antall saker"
          value={data.totaler.antall_saker}
          color="#6f42c1"
        />
        <StatCard
          icon={<AlertCircle size={24} color="#ffc107" />}
          title="Antall registreringer"
          value={data.totaler.antall_entries}
          color="#ffc107"
        />
      </div>

      {/* Eksport-knapp */}
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <button
          onClick={handleExport}
          style={{
            padding: '10px 20px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Download size={16} />
          Eksporter til CSV
        </button>
      </div>

      {/* Saker */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '20px' }}>Saker med ufakturert tid</h2>

        {data.saker.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>
            Ingen ufakturert tid registrert
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {data.saker.map(sak => (
              <div
                key={sak.sak_id}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}
              >
                {/* Sak header */}
                <div
                  onClick={() => setExpandedSak(expandedSak === sak.sak_id ? null : sak.sak_id)}
                  style={{
                    padding: '15px',
                    background: '#f8f9fa',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <strong>{sak.saksnummer}</strong> - {sak.sak_navn}
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      {sak.klient_navn}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                      {sak.total_timer.toFixed(2)} t
                    </div>
                    <div style={{ fontSize: '14px', color: '#28a745' }}>
                      kr {sak.total_verdi.toLocaleString('no-NO', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                {/* Sak detaljer (ekspanderbar) */}
                {expandedSak === sak.sak_id && (
                  <div style={{ padding: '15px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                          <th style={{ textAlign: 'left', padding: '8px' }}>Dato</th>
                          <th style={{ textAlign: 'left', padding: '8px' }}>Beskrivelse</th>
                          <th style={{ textAlign: 'right', padding: '8px' }}>Timer</th>
                          <th style={{ textAlign: 'right', padding: '8px' }}>Timepris</th>
                          <th style={{ textAlign: 'right', padding: '8px' }}>Verdi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sak.entries.map(entry => (
                          <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '8px' }}>{entry.date}</td>
                            <td style={{ padding: '8px' }}>{entry.beskrivelse}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{entry.hours.toFixed(2)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              kr {entry.hourly_rate.toLocaleString('no-NO')}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              kr {entry.verdi.toLocaleString('no-NO', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Stat Card komponent
 */
function StatCard({ icon, title, value, color }) {
  return (
    <div style={{
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      border: `2px solid ${color}20`,
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    }}>
      <div>{icon}</div>
      <div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color }}>{value}</div>
      </div>
    </div>
  );
}
